/** Lightweight, theme-consistent form primitives that play well with react-hook-form. */
import React from "react";
import clsx from "clsx";
import { Spinner } from "./Spinner";

type ButtonVariant = "primary" | "secondary" | "danger" | "success" | "ghost";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  danger: "btn-danger",
  success: "btn-success",
  ghost: "text-slate-300 hover:bg-slate-700 font-medium py-2 px-4 rounded-lg transition-colors",
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  icon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  loading,
  icon,
  children,
  className,
  disabled,
  ...props
}) => (
  <button
    {...props}
    disabled={disabled || loading}
    className={clsx(
      BUTTON_VARIANTS[variant],
      "inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60",
      className,
    )}
  >
    {loading ? <Spinner size={16} /> : icon}
    {children}
  </button>
);

interface FieldShellProps {
  label?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const Field: React.FC<FieldShellProps> = ({
  label,
  error,
  required,
  className,
  children,
}) => (
  <div className={clsx("space-y-1.5", className)}>
    {label && (
      <label className="block text-sm font-medium text-slate-300">
        {label}
        {required && <span className="ml-0.5 text-red-400">*</span>}
      </label>
    )}
    {children}
    {error && <p className="text-xs text-red-400">{error}</p>}
  </div>
);

export const TextField = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { label?: string; error?: string }
>(({ label, error, required, className, ...props }, ref) => (
  <Field label={label} error={error} required={required} className={className}>
    <input ref={ref} className="input" {...props} />
  </Field>
));
TextField.displayName = "TextField";

export const TextareaField = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    label?: string;
    error?: string;
  }
>(({ label, error, required, className, ...props }, ref) => (
  <Field label={label} error={error} required={required} className={className}>
    <textarea ref={ref} className="input min-h-[80px] resize-y" {...props} />
  </Field>
));
TextareaField.displayName = "TextareaField";

export const SelectField = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & {
    label?: string;
    error?: string;
  }
>(({ label, error, required, className, children, ...props }, ref) => (
  <Field label={label} error={error} required={required} className={className}>
    <select ref={ref} className="input" {...props}>
      {children}
    </select>
  </Field>
));
SelectField.displayName = "SelectField";
